import { getSessionId } from '@aspendental/shared-utils/utils';

import {
	DynamicFormEl,
	FormDataType,
	FormDataTypeValue,
	UserSpaceFnPipe,
	ValidUserSpaceFnName,
} from '@/components/DynamicForm/types/DynamicFormTypes';

import {
	all,
	always,
	bmiCalc,
	calcInchHt,
	concat,
	diffInYears,
	eq,
	excludes,
	excludes_i,
	getBlackboxId,
	getLiineGuid,
	gt,
	gte,
	includes,
	includes_i,
	intersection,
	intersects,
	isInteger,
	lt,
	lte,
	maxLength,
	minLength,
	none,
	oneOf,
	some,
	validUSPhoneNum,
} from './functions';
import { is, isArraysEqual, path, join, defaultTo, checkType, selectPaths, slice } from './utils';

type RuntimeFnType = (args: unknown, value: unknown) => unknown;

const applyUseQuestionStructure: RuntimeFnType = (pathKeys, formData) => {
	const key = Array.isArray(pathKeys) ? (pathKeys[0] as string) : (pathKeys as string);
	const { label, name, ...rest } = (path(['schema', key] as PropertyKey[], formData) as DynamicFormEl) || {};
	const mappedFormValue = path(pathKeys as PropertyKey[], formData);
	return (label || name) && mappedFormValue
		? Object.assign(
				{
					answer: mappedFormValue,
					question: label || name,
				},
				!(formData as FormDataType)?.exciseOptions && 'options' in rest && { options: rest.options },
				(formData as FormDataType)?.exciseOptions &&
					'unique_label' in rest && { unique_label: rest.unique_label }
		  )
		: undefined;
};

const useQuestionListStructure: RuntimeFnType = (limiter, formData) => {
	const safeLimiter = limiter ?? [];
	const isLimiterArray = Array.isArray(safeLimiter);
	const arrIndicator = isLimiterArray && safeLimiter.indexOf('asArray');
	const fmtAsArray = typeof arrIndicator === 'number' && arrIndicator > -1;
	const adjustedLimiter =
		isLimiterArray && fmtAsArray
			? safeLimiter.slice(0, arrIndicator).concat(safeLimiter.slice(arrIndicator + 1))
			: safeLimiter;
	// when present, the limiter is a filtered and ordered key list
	// when absent, fall back to the keys of formdata
	const filteredKeys: string[] =
		isLimiterArray && (adjustedLimiter as string[]).length > 0
			? (safeLimiter as string[])
			: Object.keys(formData as FormDataType);
	const isFormDataObj = is('Object')(formData);
	const adjustedFormData = { ...(formData as FormDataType), exciseOptions: fmtAsArray };

	return isLimiterArray && isFormDataObj && fmtAsArray
		? filteredKeys.flatMap((item) => applyUseQuestionStructure([item], adjustedFormData) || [])
		: isLimiterArray && isFormDataObj
		? filteredKeys.reduce((acc, item) => {
				const qst = applyUseQuestionStructure([item], formData);
				return qst ? Object.assign(acc, { [item]: qst }) : acc;
		  }, {})
		: undefined;
};

export const runtimeFns: Record<ValidUserSpaceFnName, RuntimeFnType> = {
	// needed for using an array as an argument list for some other fn.
	// specifically excludes nesting other `apply`s
	apply: (fnName, value) => {
		if (!Array.isArray(value) || !runtimeFns[fnName as ValidUserSpaceFnName] || fnName === 'apply') {
			return;
		}
		return runtimeFns[fnName as ValidUserSpaceFnName].apply(null, value as [args: unknown, value: unknown]);
	},

	always: always as RuntimeFnType,
	concat: concat as RuntimeFnType,
	defaultTo,
	join: (args, value) => (Array.isArray(value) ? join(value as unknown[][], args as string) : value),
	path: path as RuntimeFnType,
	selectPaths: selectPaths as RuntimeFnType,

	equal: (args, value) => {
		if (!Array.isArray(args)) {
			return false;
		}

		const valueToCompare: unknown = args[0];

		if (Array.isArray(value) && Array.isArray(valueToCompare)) {
			return isArraysEqual(value, valueToCompare);
		} else {
			return valueToCompare === value;
		}
	},
	// note that these comparisons are using their inversions
	// since the definitions rely on the values being the last argument
	gt: lt as RuntimeFnType,
	lt: gt as RuntimeFnType,
	gte: lte as RuntimeFnType,
	lte: gte as RuntimeFnType,
	eq: eq as RuntimeFnType,
	diffInYears: diffInYears as RuntimeFnType,
	calcInchHt: calcInchHt as RuntimeFnType,
	bmiCalc: bmiCalc as RuntimeFnType,
	includes: includes as RuntimeFnType,
	includes_i: includes_i as RuntimeFnType,
	excludes: excludes as RuntimeFnType,
	excludes_i: excludes_i as RuntimeFnType,
	oneOf: oneOf as RuntimeFnType,
	intersection: intersection as RuntimeFnType,
	intersects: intersects as RuntimeFnType,
	minLength: minLength as RuntimeFnType,
	maxLength: maxLength as RuntimeFnType,
	isInteger: isInteger as RuntimeFnType,
	validUSPhoneNum: validUSPhoneNum as RuntimeFnType,
	some: some as RuntimeFnType,
	slice: slice as RuntimeFnType,
	none: none as RuntimeFnType,
	all: all as RuntimeFnType,
	required: some as RuntimeFnType,

	getBlackboxId: getBlackboxId as RuntimeFnType,
	getLiineGuid: getLiineGuid as RuntimeFnType,
	getSessionId: getSessionId as RuntimeFnType,

	useQuestionStructure: applyUseQuestionStructure,
	useQuestionListStructure,
};

type RemapData = {
	[key: string]: unknown;
};

export const parsePiped = (arr: UserSpaceFnPipe, data: unknown): unknown =>
	arr.reduce<unknown>((acc, [fnName, args]) => {
		if (!runtimeFns[fnName]) {
			return undefined;
		}
		const fn = runtimeFns[fnName];
		return fn(args, acc);
	}, data);

type RemapActionFn = {
	(val: UserSpaceFnPipe, data: RemapData): unknown;
	(val: UserSpaceFnPipe, data: RemapData): unknown;
	(val: FormDataType, data: RemapData): unknown;
	(val: FormDataTypeValue, data: RemapData): unknown;
};

const remapActions: {
	Array: RemapActionFn;
	Object: RemapActionFn;
	default: (val: FormDataTypeValue) => unknown;
} = {
	Array: parsePiped as RemapActionFn,
	Object: remapObjectWith as RemapActionFn,
	default: (val) => val,
};

export function remapObjectWith<U extends RemapData>(structure: unknown, data: U): FormDataType {
	return Object.entries(structure as Record<string, unknown>).reduce((acc, [key, val]) => {
		const currType = checkType(val);
		const actionType = currType in remapActions ? currType : 'default';

		return Object.assign(acc, {
			[key]: remapActions[actionType as keyof typeof remapActions](val as FormDataTypeValue, data),
		});
	}, {} as FormDataType);
}
